import React, { useEffect, useRef } from 'react'
import * as d3 from 'd3'

interface D3BackgroundAnimationProps {
  width?: number
  height?: number
  transitionKey?: number // Key that changes on carousel transition
}

// Drag function helper
const createDrag = (simulation: any) => {
  function dragstarted(event: any, d: any) {
    if (!event.active) simulation.alphaTarget(0.3).restart()
    d.fx = d.x
    d.fy = d.y
  }

  function dragged(event: any, d: any) {
    d.fx = event.x
    d.fy = event.y
  }

  function dragended(event: any, d: any) {
    if (!event.active) simulation.alphaTarget(0)
    d.fx = null
    d.fy = null
  }

  return d3.drag<SVGCircleElement, any>()
    .on('start', dragstarted)
    .on('drag', dragged)
    .on('end', dragended)
}

const D3BackgroundAnimation: React.FC<D3BackgroundAnimationProps> = ({ 
  width = typeof window !== 'undefined' ? window.innerWidth : 1920, 
  height = typeof window !== 'undefined' ? window.innerHeight : 1080,
  transitionKey = 0
}) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const simulationRef = useRef<any>(null)
  const particleIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Color palettes that change with each transition
  const colorPalettes = [
    ['#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4'], // Blue-Purple-Pink-Cyan
    ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'], // Green-Blue-Orange-Red
    ['#8b5cf6', '#ec4899', '#f97316', '#eab308'], // Purple-Pink-Orange-Yellow
    ['#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'], // Cyan-Blue-Purple-Pink
    ['#f59e0b', '#ef4444', '#10b981', '#3b82f6'], // Orange-Red-Green-Blue
    ['#ec4899', '#f97316', '#06b6d4', '#8b5cf6'], // Pink-Orange-Cyan-Purple
    ['#3b82f6', '#10b981', '#f59e0b', '#ec4899'], // Blue-Green-Orange-Pink
    ['#8b5cf6', '#06b6d4', '#f97316', '#3b82f6']  // Purple-Cyan-Orange-Blue
  ]

  useEffect(() => {
    if (!svgRef.current) return

    const svg = d3.select(svgRef.current)
    
    // Get color palette based on transition key
    const currentPalette = colorPalettes[transitionKey % colorPalettes.length]
    
    // Stop previous simulation if it exists
    if (simulationRef.current) {
      simulationRef.current.stop()
    }
    if (particleIntervalRef.current) {
      clearInterval(particleIntervalRef.current)
    }

    svg.selectAll('*').remove() // Clear previous content

    // Create groups for different animation layers
    const nodesGroup = svg.append('g').attr('class', 'nodes')
    const linksGroup = svg.append('g').attr('class', 'links')
    const particlesGroup = svg.append('g').attr('class', 'particles')

    // Create force simulation
    const simulation = d3.forceSimulation()
      .force('link', d3.forceLink().id((d: any) => d.id).distance(100).strength(0.5))
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(30))

    // Create nodes (particles) - randomize positions on each transition
    const numNodes = 25
    const nodes: any[] = d3.range(numNodes).map((i) => ({
      id: i,
      radius: Math.random() * 8 + 4,
      opacity: Math.random() * 0.5 + 0.2,
      x: Math.random() * width,
      y: Math.random() * height
    }))

    // Create links between nearby nodes
    const links: any[] = []
    nodes.forEach((node, i) => {
      for (let j = i + 1; j < nodes.length; j++) {
        if (Math.random() < 0.15) { // 15% chance of connection
          links.push({ source: node.id, target: nodes[j].id })
        }
      }
    })

    // Draw links with color from palette
    const link = linksGroup
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', currentPalette[0])
      .attr('stroke-opacity', 0.3)
      .attr('stroke-width', 1.5)

    // Draw nodes
    const node = nodesGroup
      .selectAll('circle')
      .data(nodes)
      .enter()
      .append('circle')
      .attr('r', (d: any) => d.radius)
      .attr('fill', (_d: any, i: number) => {
        return currentPalette[i % currentPalette.length]
      })
      .attr('opacity', (d: any) => d.opacity)
      .call(createDrag(simulation))

    // Add glow effect to nodes
    const defs = svg.append('defs')
    const filter = defs.append('filter').attr('id', 'glow')
    filter.append('feGaussianBlur').attr('stdDeviation', '3').attr('result', 'coloredBlur')
    const feMerge = filter.append('feMerge')
    feMerge.append('feMergeNode').attr('in', 'coloredBlur')
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic')

    node.attr('filter', 'url(#glow)')

    // Store simulation reference
    simulationRef.current = simulation

    // Update simulation
    simulation.nodes(nodes as any).on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y)

      node
        .attr('cx', (d: any) => d.x)
        .attr('cy', (d: any) => d.y)
    })

    // Restart simulation with high alpha for smooth transition
    simulation.alpha(1).restart()

    // Add floating particles
    const numParticles = 15
    const particles = d3.range(numParticles).map(() => ({
      x: Math.random() * width,
      y: Math.random() * height,
      radius: Math.random() * 3 + 2,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      opacity: Math.random() * 0.4 + 0.1
    }))

    const particle = particlesGroup
      .selectAll('circle')
      .data(particles)
      .enter()
      .append('circle')
      .attr('r', (d: any) => d.radius)
      .attr('fill', currentPalette[2]) // Use third color from palette
      .attr('opacity', (d: any) => d.opacity)
      .attr('cx', (d: any) => d.x)
      .attr('cy', (d: any) => d.y)

    // Animate particles
    function animateParticles() {
      particle
        .attr('cx', (d: any) => {
          d.x += d.vx
          if (d.x < 0 || d.x > width) d.vx *= -1
          return d.x
        })
        .attr('cy', (d: any) => {
          d.y += d.vy
          if (d.y < 0 || d.y > height) d.vy *= -1
          return d.y
        })
    }

    particleIntervalRef.current = setInterval(animateParticles, 50)

    // Handle window resize
    const handleResize = () => {
      const newWidth = window.innerWidth
      const newHeight = window.innerHeight
      svg.attr('width', newWidth).attr('height', newHeight)
      simulation.force('center', d3.forceCenter(newWidth / 2, newHeight / 2))
      simulation.alpha(0.3).restart()
    }

    window.addEventListener('resize', handleResize)

    // Cleanup
    return () => {
      if (particleIntervalRef.current) {
        clearInterval(particleIntervalRef.current)
      }
      window.removeEventListener('resize', handleResize)
      if (simulationRef.current) {
        simulationRef.current.stop()
      }
    }
  }, [width, height, transitionKey])

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      className="absolute inset-0 pointer-events-none"
      style={{ opacity: 0.4 }}
    />
  )
}

export default D3BackgroundAnimation

